/**
 * Machine à états de lecture d'un replay.
 *
 * Extrait de ReplayMode pour être partagé entre la vue simple et la vue
 * « double tables ». L'historique fourni est l'historique primaire : il définit
 * la timeline navigable et les délais de lecture automatique. En vue double, la
 * position est ensuite rejouée sur la seconde table via le curseur logique.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type { GameHistory, ReplayStep } from '../game/gameHistory';
import type { ReplayCursor } from '../game/replayCursor';
import { cursorFromPosition, maxSubStepForStep } from '../game/replayCursor';

const MIN_BOT_DELAY = 800;
const TRICK_TRANSITION_DELAY = 1500;
const STEP_HEADER_DELAY = 2000;
const HARMONIZED_CARD_DELAY = 600;
const TIMELINE_CLICK_CARD_DELAY = 120;
const HARMONIZED_STEP_DELAY = 1500;

function isEditableElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}

export interface ReplayPlayback {
  steps: ReplayStep[];
  stepIndex: number;
  subStep: number;
  maxSubStep: number;
  currentStep: ReplayStep;
  cursor: ReplayCursor;
  isPlaying: boolean;
  speed: number;
  setSpeed: (speed: number) => void;
  harmonizedDelays: boolean;
  setHarmonizedDelays: (value: boolean) => void;
  atStart: boolean;
  atEnd: boolean;
  nextSubStep: () => boolean;
  prevSubStep: () => boolean;
  handlePrevTrick: () => void;
  handleNextTrick: () => void;
  handleTimelineClick: (index: number) => void;
  handleRangeChange: (index: number) => void;
  toggleAutoPlay: () => void;
  stopAutoPlay: () => void;
  /** À attacher sur la zone qui capte molette et swipe. */
  gestureRef: React.RefObject<HTMLDivElement | null>;
}

export function useReplayPlayback(history: GameHistory): ReplayPlayback {
  const { steps } = history;

  const [stepIndex, setStepIndex] = useState(0);
  const [subStep, setSubStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [harmonizedDelays, setHarmonizedDelays] = useState(true);

  const autoPlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timelineAnimTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPlayingRef = useRef(false);
  isPlayingRef.current = isPlaying;

  const currentStep = steps[stepIndex] || steps[0];

  const maxSubStep = useMemo(
    () => maxSubStepForStep(history, currentStep),
    [history, currentStep],
  );

  const cursor = useMemo(
    () => cursorFromPosition(history, stepIndex, subStep),
    [history, stepIndex, subStep],
  );

  // Changement d'historique (donne suivante) : la position est conservée mais
  // ramenée dans les bornes de la nouvelle timeline.
  useEffect(() => {
    if (steps.length > 0 && stepIndex > steps.length - 1) {
      setStepIndex(steps.length - 1);
      setSubStep(0);
    }
  }, [steps, stepIndex]);

  // ==========================================
  // Navigation
  // ==========================================

  const nextSubStep = useCallback(() => {
    if (currentStep?.type === 'trick' && subStep < maxSubStep) {
      setSubStep(subStep + 1);
      return true;
    }
    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1);
      setSubStep(0);
      return true;
    }
    return false;
  }, [currentStep, subStep, maxSubStep, stepIndex, steps]);

  const prevSubStep = useCallback(() => {
    if (subStep > 0) {
      setSubStep(subStep - 1);
      return true;
    }
    if (stepIndex > 0) {
      const prevIdx = stepIndex - 1;
      const prevStep = steps[prevIdx];
      setStepIndex(prevIdx);
      if (prevStep.type === 'trick' && prevStep.trickNumber) {
        const trick = history.tricks.find(t => t.trickNumber === prevStep.trickNumber);
        setSubStep(trick ? trick.cards.length : 0);
      } else {
        setSubStep(0);
      }
      return true;
    }
    return false;
  }, [subStep, stepIndex, steps, history.tricks]);

  // ==========================================
  // Auto-play
  // ==========================================

  const stopAutoPlay = useCallback(() => {
    setIsPlaying(false);
    if (autoPlayTimerRef.current) {
      clearTimeout(autoPlayTimerRef.current);
      autoPlayTimerRef.current = null;
    }
    if (timelineAnimTimerRef.current) {
      clearTimeout(timelineAnimTimerRef.current);
      timelineAnimTimerRef.current = null;
    }
  }, []);

  /**
   * Animate cards one by one for a given step (used by timeline click, prev/next trick).
   * If harmonizedDelays is checked, uses a uniform TIMELINE_CLICK_CARD_DELAY.
   * Otherwise, uses the actual recorded delays between cards (scaled by speed).
   */
  const animateStepCards = useCallback((index: number) => {
    if (timelineAnimTimerRef.current) {
      clearTimeout(timelineAnimTimerRef.current);
      timelineAnimTimerRef.current = null;
    }

    const step = steps[index];
    setStepIndex(index);
    setSubStep(0);

    if (step?.type === 'trick' && step.trickNumber) {
      const trick = history.tricks.find(t => t.trickNumber === step.trickNumber);
      if (trick && trick.cards.length > 0) {
        let currentSub = 0;
        const advanceCard = () => {
          currentSub++;
          if (currentSub <= trick.cards.length) {
            setSubStep(currentSub);
            if (currentSub < trick.cards.length) {
              let delay: number;
              if (harmonizedDelays) {
                delay = TIMELINE_CLICK_CARD_DELAY;
              } else {
                const currentTs = trick.cards[currentSub - 1]?.timestamp || 0;
                const nextTs = trick.cards[currentSub]?.timestamp || 0;
                const realDelay = nextTs - currentTs;
                delay = Math.max(TIMELINE_CLICK_CARD_DELAY, realDelay / speed);
              }
              timelineAnimTimerRef.current = setTimeout(advanceCard, delay);
            }
          }
        };
        timelineAnimTimerRef.current = setTimeout(advanceCard, TIMELINE_CLICK_CARD_DELAY);
      }
    }
  }, [steps, history.tricks, harmonizedDelays, speed]);

  const handlePrevTrick = useCallback(() => {
    stopAutoPlay();
    if (stepIndex > 0) {
      animateStepCards(stepIndex - 1);
    }
  }, [stepIndex, stopAutoPlay, animateStepCards]);

  const handleNextTrick = useCallback(() => {
    stopAutoPlay();
    if (stepIndex < steps.length - 1) {
      animateStepCards(stepIndex + 1);
    }
  }, [stepIndex, steps.length, stopAutoPlay, animateStepCards]);

  const handleTimelineClick = useCallback((index: number) => {
    stopAutoPlay();
    animateStepCards(index);
  }, [stopAutoPlay, animateStepCards]);

  const handleRangeChange = useCallback((index: number) => {
    stopAutoPlay();
    animateStepCards(index);
  }, [stopAutoPlay, animateStepCards]);

  const scheduleNextAutoPlay = useCallback(() => {
    if (!isPlayingRef.current) return;

    let delay: number;

    if (harmonizedDelays) {
      if (currentStep?.type !== 'trick') {
        delay = HARMONIZED_STEP_DELAY;
      } else if (subStep < maxSubStep) {
        delay = HARMONIZED_CARD_DELAY;
      } else {
        delay = HARMONIZED_STEP_DELAY;
      }
    } else {
      if (currentStep?.type !== 'trick') {
        delay = STEP_HEADER_DELAY;
      } else if (subStep < maxSubStep) {
        const trick = history.tricks.find(t => t.trickNumber === currentStep.trickNumber);
        if (trick && subStep < trick.cards.length - 1) {
          const currentCard = trick.cards[subStep];
          const nextCard = trick.cards[subStep + 1];
          delay = Math.max(MIN_BOT_DELAY, nextCard.timestamp - currentCard.timestamp);
        } else {
          delay = MIN_BOT_DELAY;
        }
      } else {
        delay = TRICK_TRANSITION_DELAY;
      }
    }

    delay = Math.round(delay / speed);

    autoPlayTimerRef.current = setTimeout(() => {
      autoPlayTimerRef.current = null;
      if (!isPlayingRef.current) return;

      const hasMore = nextSubStep();
      if (hasMore) {
        scheduleNextAutoPlay();
      } else {
        stopAutoPlay();
      }
    }, delay);
  }, [currentStep, subStep, maxSubStep, speed, harmonizedDelays, history.tricks, nextSubStep, stopAutoPlay]);

  const toggleAutoPlay = useCallback(() => {
    if (isPlaying) {
      stopAutoPlay();
    } else {
      setIsPlaying(true);
      if (stepIndex === steps.length - 1 && subStep >= maxSubStep) {
        setStepIndex(0);
        setSubStep(0);
      }
    }
  }, [isPlaying, stopAutoPlay, stepIndex, steps.length, subStep, maxSubStep]);

  useEffect(() => {
    if (isPlaying) {
      scheduleNextAutoPlay();
    }
    return () => {
      if (autoPlayTimerRef.current) {
        clearTimeout(autoPlayTimerRef.current);
        autoPlayTimerRef.current = null;
      }
    };
  }, [isPlaying, stepIndex, subStep, scheduleNextAutoPlay]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEditableElement(e.target)) return;

      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          if (!isPlaying) nextSubStep();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (!isPlaying) prevSubStep();
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!isPlaying) handleNextTrick();
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!isPlaying) handlePrevTrick();
          break;
        case 'p':
          e.preventDefault();
          toggleAutoPlay();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying, nextSubStep, prevSubStep, handleNextTrick, handlePrevTrick, toggleAutoPlay]);

  // Wheel & touch scroll navigation
  const gestureRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = gestureRef.current;
    if (!el) return;

    // Vertical: accumulated delta approach for smooth trackpad control
    let accumulatedDeltaY = 0;
    let lastYScrollTime = 0;
    const DELTA_Y_THRESHOLD = 120;
    const Y_RESET_MS = 400;

    // Horizontal: throttle
    let lastWheelTimeX = 0;
    const WHEEL_THROTTLE_X = 250;

    const handleWheel = (e: WheelEvent) => {
      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      if (absX < 2 && absY < 2) return;

      e.preventDefault();
      const now = Date.now();

      if (absY >= absX) {
        if (now - lastYScrollTime > Y_RESET_MS) accumulatedDeltaY = 0;
        lastYScrollTime = now;
        accumulatedDeltaY += e.deltaY;
        if (Math.abs(accumulatedDeltaY) >= DELTA_Y_THRESHOLD) {
          if (accumulatedDeltaY > 0) handleNextTrick();
          else handlePrevTrick();
          accumulatedDeltaY = 0;
        }
      } else {
        if (now - lastWheelTimeX < WHEEL_THROTTLE_X) return;
        lastWheelTimeX = now;
        stopAutoPlay();
        // Scroll left (deltaX < 0, finger right) = next card
        if (e.deltaX < 0) nextSubStep();
        else prevSubStep();
      }
    };

    // Touch swipe support
    let touchStartX = 0;
    let touchStartY = 0;
    let touchHandled = false;
    const SWIPE_THRESHOLD = 30;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchHandled = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchHandled) return;
      const dx = e.touches[0].clientX - touchStartX;
      const dy = e.touches[0].clientY - touchStartY;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      if (absX < SWIPE_THRESHOLD && absY < SWIPE_THRESHOLD) return;

      e.preventDefault();
      touchHandled = true;

      if (absY >= absX) {
        // Swipe up (dy<0) = scroll down = next trick
        if (dy < 0) handleNextTrick();
        else handlePrevTrick();
      } else {
        stopAutoPlay();
        // Swipe left (dx<0) = scroll right = next card
        if (dx < 0) nextSubStep();
        else prevSubStep();
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
    };
  }, [handleNextTrick, handlePrevTrick, stopAutoPlay, nextSubStep, prevSubStep]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoPlayTimerRef.current) clearTimeout(autoPlayTimerRef.current);
      if (timelineAnimTimerRef.current) clearTimeout(timelineAnimTimerRef.current);
    };
  }, []);

  return {
    steps,
    stepIndex,
    subStep,
    maxSubStep,
    currentStep,
    cursor,
    isPlaying,
    speed,
    setSpeed,
    harmonizedDelays,
    setHarmonizedDelays,
    atStart: stepIndex === 0 && subStep === 0,
    atEnd: stepIndex === steps.length - 1 && subStep >= maxSubStep,
    nextSubStep,
    prevSubStep,
    handlePrevTrick,
    handleNextTrick,
    handleTimelineClick,
    handleRangeChange,
    toggleAutoPlay,
    stopAutoPlay,
    gestureRef,
  };
}
